import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSiteSettings } from '@/lib/siteSettings';
import { supabase } from '@/lib/supabase';
import { sendInquiryEmail } from '@/lib/web3forms';
import { motion } from 'motion/react';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { settings } = useSiteSettings();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('company_alt') as string;
    if (hp && hp.trim() !== '') {
      setSubmitted(true);
      return;
    }

    const name = (formData.get('name') as string) || '';
    const email = (formData.get('email') as string) || '';
    const company = (formData.get('company') as string) || '';
    const phone = (formData.get('phone') as string) || '';
    const interest = (formData.get('interest') as string) || '';
    const message = (formData.get('message') as string) || '';

    try {
      const { error: supabaseErr } = await supabase.from('inquiries').insert({
        name,
        email,
        company: company || null,
        phone: phone || null,
        product_interest: interest || null,
        message: message || null,
        status: 'new',
      });
      if (supabaseErr) console.error('Supabase insert error:', supabaseErr);
    } catch (err) {
      console.error('Supabase insert failed:', err);
    }

    const result = await sendInquiryEmail({ name, email, company, phone, interest, message });

    if (result.ok) {
      setSubmitted(true);
      setError('');
      form.reset();
    } else {
      setError(result.message);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Header */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-primary-900 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary-500/5 rounded-full blur-[100px]"></div>
          </div>
          <div className="relative z-10">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4"
            >
              Get In Touch
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-5 leading-tight"
            >
              Let&apos;s Build the<br />
              <span className="text-accent-400">Future Together</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed"
            >
              Every successful partnership begins with a conversation. Whether you&apos;re sourcing quality products, managing complex logistics, or seeking a dependable procurement partner, we are ready to help you achieve your business objectives with confidence.
            </motion.p>
          </div>
        </section>

        {/* Offices + Form */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">

            {/* Left Column - Offices */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="space-y-10"
            >
              {/* Kenya Office */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-5">Kenya Office</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-800 leading-relaxed">
                        Cotts House, Moi Avenue,<br />Majengo, Mvita,<br />Mombasa, Kenya
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <a href="tel:+254715815521" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">+254 715 815 521</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* China Office */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-5">China Office</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-800 leading-relaxed">
                        No. 14, Huangmaoshu Industrial Zone,<br />Lishui District, Foshan City,<br />Guangdong Province, China
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href="tel:+8613025117490" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">+86 130 2511 7490</a>
                      <a
                        href="https://wa.me/8613025117490"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] text-white hover:bg-[#1fb855] transition-colors flex-shrink-0 cursor-pointer"
                        title="Chat on WhatsApp"
                      >
                        <i className="ri-whatsapp-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Contact */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-5">General Inquiries</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div className="flex flex-col gap-1">
                      <a href="mailto:info@bekaaliglobal.com" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">info@bekaaliglobal.com</a>
                      <a href="mailto:director@bekaaliglobal.com" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">director@bekaaliglobal.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-global-line"></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-800">bekaaliglobal.com</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">Office Hours</h3>
                <p className="text-sm text-foreground-600">Monday - Friday, 8:00 AM - 6:00 PM EAT</p>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-2"
            >
              {submitted ? (
                <div className="bg-background-100 border border-background-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-2">Thank You!</h3>
                  <p className="text-sm text-foreground-500">Your inquiry has been received. Our team will get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" className="bg-background-100 border border-background-200/70 rounded-2xl p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">Send Us a Message</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Full Name *</label>
                        <input type="text" name="name" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800" placeholder="John Doe" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Company *</label>
                        <input type="text" name="company" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800" placeholder="Your Company Ltd" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email *</label>
                        <input type="email" name="email" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800" placeholder="you@company.com" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">Phone</label>
                        <input type="tel" name="phone" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800" placeholder="+254 700 000 000" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">Product / Service Interest</label>
                      <select name="interest" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors text-foreground-800 cursor-pointer">
                        <option value="">Select an option</option>
                        <option value="trading">Trading</option>
                        <option value="procurement">Procurement</option>
                        <option value="supply-chain">Supply Chain Management</option>
                        <option value="freight">Freight Forwarding</option>
                        <option value="clearing">Clearing & Forwarding</option>
                        <option value="other">Other / Multiple</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">Message *</label>
                      <textarea name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 transition-colors resize-none text-foreground-800" placeholder="Tell us about your requirements..."></textarea>
                    </div>
                    <div className="hp-field">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    {error && <div className="text-xs text-red-500 bg-red-50 px-4 py-2.5 rounded-lg">{error}</div>}
                    <button type="submit" className="w-full px-6 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                      Send Inquiry <i className="ri-send-plane-line ml-2"></i>
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>

      </main>
      <Footer />
    </>
  );
}